/**
 * Das Übersetzungs-Bündel einer Sprache (`/api/i18n/translations/<sprache>`),
 * als stabiles Promise — für `OeffentlicheSeite` (Server und Browser, über
 * `use()`) und für die Server-Teile der öffentlichen Seiten, die Texte vor dem
 * Rendern brauchen (Titel des Vorschaubilds, Brotkrumen der Strukturdaten,
 * `app/_seo/woerterbuch.ts`).
 *
 * Kein `'use client'`: Ein Server-Modul darf eine Funktion aus einem
 * Client-Modul nicht aufrufen (es bekommt nur eine Referenz). Server- und
 * Browser-Seite halten je ihre eigene Liste.
 */

/**
 * Wie lange der Server ein Bündel OHNE Rückfrage wiederverwendet. Das ist kein
 * Cache, sondern Render-Mechanik: `use()` braucht über den Suspend-Neustart
 * desselben Renderns hinweg dasselbe Promise. Danach fragt der Server beim
 * Backend nach (`If-None-Match`) — Redis dort ist die EINE Cache-Schicht und
 * wird nach jedem Schreibweg geleert, auch nach Migrationen beim Containerstart.
 * Bis 14.10.2026 hielt der Server das Bündel hier 60 s ohne Rückfrage fest:
 * nach einem Backend-Deploy mit neuen Texten noch eine Minute der alte Stand.
 */
const SERVER_RENDER_FENSTER_MS = 2_000;

interface Buendel {
  geholt: number;
  daten: Promise<Record<string, string>>;
  /** Erst gesetzt, wenn der Abruf durch ist — für die ETag-Rückfrage. */
  stand?: { etag: string; texte: Record<string, string> };
}

const buendel = new Map<string, Buendel>();

/**
 * Das Übersetzungs-Bündel einer Sprache, als stabiles Promise für `use()`.
 * Auf dem Server direkt beim Backend (`BACKEND_INTERNAL_URL`), im Browser über
 * denselben relativen Pfad wie `useTranslation`. Ein Fehlschlag wirft — eine
 * Seite mit deutschen Vorgabetexten unter einer französischen Adresse wäre
 * ein stiller Fehler (Regel #7).
 */
export function uebersetzungen(sprache: string): Promise<Record<string, string>> {
  const imBrowser = typeof window !== 'undefined';
  const vorhanden = buendel.get(sprache);
  if (vorhanden && (imBrowser || Date.now() - vorhanden.geholt < SERVER_RENDER_FENSTER_MS)) {
    return vorhanden.daten;
  }
  const basis = imBrowser ? '' : (process.env.BACKEND_INTERNAL_URL ?? '').replace(/\/$/, '');
  if (!imBrowser && !basis) {
    throw new Error('BACKEND_INTERNAL_URL ist nicht gesetzt (docker-compose: Dienst next)');
  }
  const bisher = imBrowser ? undefined : vorhanden?.stand;
  const eintrag: Buendel = { geholt: Date.now(), daten: Promise.resolve({}) };
  eintrag.daten = fetch(`${basis}/api/i18n/translations/${encodeURIComponent(sprache)}`, {
    // Kein zweiter Cache im Next-Datencache — siehe SERVER_RENDER_FENSTER_MS.
    ...(imBrowser ? {} : { cache: 'no-store' as const }),
    ...(bisher ? { headers: { 'If-None-Match': bisher.etag } } : {}),
  }).then(async (antwort) => {
    if (antwort.status === 304 && bisher) {
      eintrag.stand = bisher;
      return bisher.texte;
    }
    if (!antwort.ok) {
      throw new Error(`/api/i18n/translations/${sprache} antwortete mit ${antwort.status}`);
    }
    const json = (await antwort.json()) as Record<string, string> | { translations: Record<string, string> };
    const texte =
      'translations' in json && typeof json.translations === 'object'
        ? json.translations
        : (json as Record<string, string>);
    const etag = antwort.headers.get('etag');
    if (etag) eintrag.stand = { etag, texte };
    return texte;
  });
  // Auf dem Server bleibt ein gescheiterter Abruf nicht im Speicher — die
  // nächste Anfrage fragt neu. Im Browser bleibt er stehen: Jedes neue
  // Rendern nach dem Fehler hätte sonst einen neuen Abruf gestartet, der
  // wieder scheitert (lokal gemessen: Hunderte 404 in Folge). Der Fehler
  // geht an die Fehlergrenze, ein Neuladen fragt neu.
  eintrag.daten.catch((fehler: unknown) => {
    if (imBrowser) {
      console.error('Übersetzungen nicht geladen', fehler);
    } else {
      buendel.delete(sprache);
    }
  });
  buendel.set(sprache, eintrag);
  return eintrag.daten;
}
